/**
 * CG-7 — onboarding step-0 fork (founder-ratified 2026-07-04): before
 * the athlete wizard, a signed-in first-run account declares who it's
 * for. "Athlete" continues into the existing 7-step flow; "parent or
 * guardian" persists `_account_role = 'guardian'` and routes to
 * /guardian (the pre-link invite state) — fixing the guardian-lands-
 * in-athlete-onboarding wart at the root.
 *
 * Presentational: the persist + navigation live in the Onboarding
 * route; this component only renders the choice (shared StepLayout
 * primitives, no custom chrome).
 */

import { useState } from 'react';
import { OptionRow } from '../primitives';
import { StepCta, StepHeading, StepStack, StepSub } from '../OnboardingShell/StepLayout';
import type { ForkRole } from './forkStorage';
import styles from './StepZeroFork.module.css';

export type { ForkRole } from './forkStorage';

export interface StepZeroForkProps {
  onChoose: (role: ForkRole) => void;
  busy: boolean;
  error: string | null;
  /** P7(a) — render the "I'm the coach" card (flag coach_provisioning_v1).
   *  The fork screen literally asks "Who is this account for?" — a coach is
   *  neither athlete nor guardian, so the coach path forks HERE, not one
   *  screen deeper on the athlete journey picker. */
  coachOption?: boolean;
  /** B456 — the highlighted choice, when the ROUTE holds it. The pick used to
   *  live only in this component, so any moment the route returned null (a
   *  relationships or context refetch, an auth settle) unmounted the screen and
   *  the athlete's tap with it: the next Continue landed on a screen that had
   *  forgotten it. Held above, a remount keeps the choice. Optional: without
   *  both props the screen keeps its own pick, as before. */
  selected?: ForkRole | null;
  onSelect?: (role: ForkRole) => void;
}

export function StepZeroFork({
  onChoose,
  busy,
  error,
  coachOption = false,
  selected: selectedProp,
  onSelect,
}: StepZeroForkProps) {
  const [localSelected, setLocalSelected] = useState<ForkRole | null>(null);
  const selected = onSelect !== undefined && selectedProp !== undefined ? selectedProp : localSelected;
  const setSelected = (role: ForkRole) => {
    if (onSelect !== undefined && selectedProp !== undefined) onSelect(role);
    else setLocalSelected(role);
  };

  return (
    <>
      <StepHeading>Who is this account for?</StepHeading>
      <StepSub>You can always add the other role later.</StepSub>

      <StepStack>
        <OptionRow
          label="I&rsquo;m the athlete"
          sub="Set up your own training."
          selected={selected === 'athlete'}
          onClick={() => setSelected('athlete')}
        />
        <OptionRow
          label="I&rsquo;m a parent or guardian"
          sub="Support your child&rsquo;s training. You invite them by email, and they confirm the connection."
          selected={selected === 'guardian'}
          onClick={() => setSelected('guardian')}
        />
        {coachOption && (
          <OptionRow
            label="I&rsquo;m the coach"
            sub="Set up your team; invite athletes by email. They get your invite when they sign in, and join once they accept."
            selected={selected === 'coach'}
            onClick={() => setSelected('coach')}
          />
        )}
      </StepStack>

      <StepCta
        onClick={() => selected && onChoose(selected)}
        disabled={!selected || busy}
      >
        {busy ? 'Saving…' : 'Continue'}
      </StepCta>

      {error && (
        <div className={styles.errorChip} role="alert" aria-live="assertive">
          {error}
        </div>
      )}
    </>
  );
}
